import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { TopNav } from '@/components/layout/TopNav';
import { Landing } from '@/views/Landing';
import { Login } from '@/views/Login';
import { Onboard } from '@/views/Onboard';
import { Kyc } from '@/views/Kyc';
import { Processing } from '@/views/Processing';
import { Report } from '@/views/Report';
import { ReportHistory } from '@/views/ReportHistory';
import { Modals } from '@/components/shared/Modal';
import { CommandPalette } from '@/components/shared/CommandPalette';
import { Toasts } from '@/components/shared/Toasts';
import { $isAuthenticated } from '@/model/auth';
import { resumeFx } from '@/model/search';
import { $view, PROTECTED_VIEWS } from '@/model/router';
import { cmdkClosed, cmdkToggled, $cmdkOpen } from '@/model/cmdk';
import { themeToggled } from '@/model/theme';
import { modalClosed } from '@/model/modals';

const VIEW_MAP = {
  landing: Landing,
  login: Login,
  onboard: Onboard,
  kyc: Kyc,
  processing: Processing,
  report: Report,
  history: ReportHistory,
} as const;

const Router = () => {
  const [view, isAuth] = useUnit([$view, $isAuthenticated]);
  if (PROTECTED_VIEWS.has(view) && !isAuth) {
    return <Login />;
  }
  const Active = VIEW_MAP[view];
  return <Active />;
};

const KeyboardShortcuts = () => {
  const cmdkOpen = useUnit($cmdkOpen);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        cmdkToggled();
        return;
      }
      if (e.key === 'Escape') {
        cmdkClosed();
        modalClosed();
        return;
      }
      if (e.key === 't' && !e.metaKey && !e.ctrlKey && !cmdkOpen) {
        const tag = (document.activeElement?.tagName ?? '').toUpperCase();
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
          themeToggled();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cmdkOpen]);

  return null;
};

const ResumeOnBoot = () => {
  const isAuth = useUnit($isAuthenticated);
  useEffect(() => {
    if (isAuth) {
      // Если уже залогинены при загрузке страницы — узнать что с поиском
      // и переместить пользователя на /processing (idle search) или /report.
      void resumeFx();
    }
    // намеренно один раз при mount: повторный isAuth-flip обрабатывает sample loginFx→resumeFx
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TopNav />
      <main>
        <Router />
      </main>
      <Modals />
      <CommandPalette />
      <Toasts />
      <KeyboardShortcuts />
      <ResumeOnBoot />
    </ThemeProvider>
  </QueryClientProvider>
);
